import { useState } from "react";
import { toast } from "sonner";
import { X, Plus } from "@phosphor-icons/react";
import {
  PhysicalCatalogPickers,
  PartnerCatalogPickers,
  KinkCatalogPicker,
} from "../catalog/CatalogTagPickers";
import PlacePicker from "./PlacePicker";
import { formatPlaceAddress } from "../../services/placeService";

function TagInput({ label, values, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState("");
  function add() {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) return;
    onChange([...values, v]);
    setDraft("");
  }
  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="zx-label">{label}</label>
      <div className="flex flex-wrap gap-2">
        {values.map((t) => (
          <span key={t} className="zx-tag accent">
            {t}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== t))}
              className="ml-1 opacity-70 hover:opacity-100"
              aria-label={`Quitar ${t}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          className="zx-input"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          onClick={add}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(184,50,90,0.45)] bg-[rgba(139,30,63,0.18)] text-[#e9b5c5] hover:bg-[rgba(139,30,63,0.32)]"
          aria-label="Añadir"
        >
          <Plus size={14} weight="thin" />
        </button>
      </div>
    </div>
  );
}

export default function CreateMeetingForm({ defaults, onSubmit, catalogs, radiusKm = 25 }) {
  const [type, setType] = useState("1+1");
  const [address, setAddress] = useState("");
  const [time, setTime] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [physicalAttributes, setPhysicalAttributes] = useState(defaults.physicalAttributes || []);
  const [preferences, setPreferences] = useState(defaults.preferences || []);
  const [interests, setInterests] = useState(defaults.interests || []);
  const [submitting, setSubmitting] = useState(false);

  function handlePlaceSelect(place) {
    setSelectedPlace(place);
    if (place) {
      setAddress(formatPlaceAddress(place));
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!selectedPlace?.uuid) {
      toast.error("Selecciona un hospedaje de la lista o del mapa.");
      return;
    }
    if (!address || !time || !identifier) return;
    setSubmitting(true);
    try {
      await onSubmit({
        type,
        address,
        time: new Date(time).toISOString(),
        identifier,
        exactLat: selectedPlace.lat,
        exactLng: selectedPlace.lng,
        accommodationId: selectedPlace.uuid,
        placeUuid: selectedPlace.uuid,
        placeName: selectedPlace.name,
        placeLocality: selectedPlace.addressLocality,
        physicalAttributes,
        preferences,
        interests,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-7 zx-stagger" data-testid="create-meeting-form">
      <div className="zx-card p-5 space-y-5">
        <div>
          <label className="zx-label">Tipo de encuentro</label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {["1+1", "2+2"].map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setType(opt)}
                data-testid={`type-${opt}`}
                className={[
                  "rounded-full border px-4 py-3 text-[11px] uppercase tracking-[0.22em] transition-all",
                  type === opt
                    ? "border-[rgba(184,50,90,0.6)] bg-[rgba(139,30,63,0.22)] text-[#f5f5f5]"
                    : "border-[rgba(255,255,255,0.12)] text-[rgba(255,255,255,0.65)] hover:text-[#f5f5f5]",
                ].join(" ")}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <PlacePicker
          radiusKm={radiusKm}
          value={selectedPlace}
          onChange={handlePlaceSelect}
        />

        {selectedPlace && (
          <p
            className="text-[10px] uppercase tracking-[0.22em] text-[#e9b5c5]"
            data-testid="selected-accommodation-label"
          >
            Hospedaje: {selectedPlace.name}
          </p>
        )}

        <div className="space-y-1">
          <label className="zx-label" htmlFor="addr">Dirección exacta (privada)</label>
          <input
            id="addr"
            className="zx-input"
            placeholder="Se rellena al elegir un hotel…"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            data-testid="meeting-address-input"
          />
          <p className="text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.4)]">
            Se completa al seleccionar un hospedaje. Solo visible para participantes confirmados.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <label className="zx-label" htmlFor="time">Fecha y hora</label>
            <input
              id="time"
              type="datetime-local"
              className="zx-input"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="meeting-time-input"
            />
          </div>
          <div className="space-y-1">
            <label className="zx-label" htmlFor="ident">Identificador visible</label>
            <input
              id="ident"
              className="zx-input"
              placeholder="Ej. pañuelo burdeos"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              data-testid="meeting-identifier-input"
            />
          </div>
        </div>
      </div>

      <div className="zx-card p-5 space-y-6">
        <p className="zx-label">Características y deseos</p>
        <div className="space-y-3">
          <PhysicalCatalogPickers
            catalogData={catalogs?.physical}
            isLoading={catalogs?.isLoading}
            values={physicalAttributes}
            onChange={setPhysicalAttributes}
            testIdPrefix="meeting-catalog-physical"
          />
          <TagInput
            label="Características físicas buscadas"
            values={physicalAttributes}
            onChange={setPhysicalAttributes}
            placeholder="Ej. Atlético, moreno..."
            testId="tags-physical"
          />
        </div>
        <div className="space-y-3">
          <PartnerCatalogPickers
            catalogData={catalogs?.partnerPreferences}
            isLoading={catalogs?.isLoading}
            values={preferences}
            onChange={setPreferences}
            testIdPrefix="meeting-catalog-partner"
          />
          <TagInput
            label="Preferencias"
            values={preferences}
            onChange={setPreferences}
            placeholder="Ej. Discreción, velas..."
            testId="tags-preferences"
          />
        </div>
        <div className="space-y-3">
          <KinkCatalogPicker
            catalogData={catalogs?.kinks}
            isLoading={catalogs?.isLoading}
            values={interests}
            onChange={setInterests}
            testIdPrefix="meeting-catalog-kinks"
          />
          <TagInput
            label="Gustos / intereses"
            values={interests}
            onChange={setInterests}
            placeholder="Ej. Sensorial, juego de roles..."
            testId="tags-interests"
          />
        </div>
      </div>

      <button
        type="submit"
        className="zx-btn-primary"
        disabled={submitting || !selectedPlace?.uuid}
        data-testid="meeting-submit-button"
      >
        {submitting ? "Creando..." : "Publicar encuentro"}
      </button>
    </form>
  );
}
